<template>
    <v-dialog v-model="dialogValue" max-width="520" :fullscreen="isMobile">
        <DialogLayout
            title="Get Early Access"
            subtitle="Get early access updates and launch-day perks"
            card-class="landing-dialog-card"
            @close="close">
            <div v-if="hasSubmitted" class="py-4 text-center">
                <div class="d-flex mb-4 justify-center">
                    <v-avatar :color="alreadyRegistered ? 'info' : 'success'" size="64" variant="flat">
                        <v-icon
                            :icon="alreadyRegistered ? 'mdi-check-circle-outline' : 'mdi-party-popper'"
                            size="32"
                            :class="{ heartbeat: !alreadyRegistered }" />
                    </v-avatar>
                </div>
                <div class="text-h6 font-weight-bold mb-2">
                    <template v-if="alreadyRegistered">You're already on the waitlist</template>
                    <template v-else>You're on the list!</template>
                </div>
                <div class="text-body-2 text-medium-emphasis">
                    <template v-if="alreadyRegistered">We'll be in touch soon.</template>
                    <template v-else>Check your email for more details!</template>
                </div>
            </div>

            <template v-else>
                <p class="text-body-2 text-medium-emphasis mb-4">
                    Early adopters get special pricing and the chance to shape Mix n' Batch with direct feedback.
                </p>

                <div class="d-flex flex-column ga-2 mb-4">
                    <div class="d-flex align-center ga-2">
                        <v-icon size="20">mdi-tag-outline</v-icon>
                        <span>Special early-adopter pricing</span>
                    </div>
                    <div class="d-flex align-center ga-2">
                        <v-icon size="20">mdi-lightbulb-outline</v-icon>
                        <span>Influence the roadmap</span>
                    </div>
                    <div class="d-flex align-center ga-2">
                        <v-icon size="20">mdi-email-outline</v-icon>
                        <span>Launch updates (no spam)</span>
                    </div>
                </div>

                <v-form @submit.prevent="submit">
                    <v-text-field
                        ref="emailInput"
                        v-model="email"
                        :disabled="submitting"
                        :error-messages="emailError ? [emailError] : []"
                        autocomplete="email"
                        density="compact"
                        label="Email address"
                        placeholder="you@bakery.com"
                        required
                        type="email"
                        variant="outlined" />
                    <v-textarea
                        v-model="comments"
                        :disabled="submitting"
                        auto-grow
                        density="compact"
                        label="Comments? We'd love to hear what you're thinking"
                        rows="3"
                        variant="outlined" />
                    <v-checkbox
                        v-model="contactAboutBeta"
                        :disabled="submitting"
                        density="compact"
                        hide-details
                        label="Contact me about participating in the beta" />
                </v-form>
            </template>

            <template #actions>
                <v-spacer />
                <v-btn variant="text" :disabled="submitting" @click="close">
                    {{ hasSubmitted ? 'Close' : 'Cancel' }}
                </v-btn>
                <v-btn v-if="!hasSubmitted" color="primary" variant="text" :disabled="!canSubmit" :loading="submitting" @click="submit">
                    Join waitlist
                </v-btn>
            </template>
        </DialogLayout>
    </v-dialog>
</template>

<script lang="ts" setup>
    import DialogLayout from '@/components/global/DialogLayout.vue';
    import { useNotifications } from '@/composables/useNotifications';
    import { useLandingIsMobile } from '@/pages/Landing/composables/useLandingIsMobile';
    import { validateEmail } from '@/pages/Landing/utils/landingValidators';
    import { waitlistEntryService } from '@/services/WaitlistEntryService';
    import { fireConfetti } from '@/utils/fireConfetti';
    import { computed, nextTick, ref, watch } from 'vue';

    const props = defineProps<{
        modelValue: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    const dialogValue = computed({
        get: () => props.modelValue,
        set: (value: boolean) => emit('update:modelValue', value),
    });

    const close = () => {
        dialogValue.value = false;
    };

    const { isMobile } = useLandingIsMobile();

    const { error: notifyError, success: notifySuccess } = useNotifications();

    const emailInput = ref<InstanceType<(typeof import('vuetify/components'))['VTextField']> | null>(null);
    const email = ref('');
    const contactAboutBeta = ref(false);
    const comments = ref('');
    const emailError = ref<string | null>(null);
    const submitting = ref(false);
    const hasSubmitted = ref(false);
    const alreadyRegistered = ref(false);

    const canSubmit = computed(() => {
        return !submitting.value && validateEmail(email.value) === null;
    });

    const resetState = () => {
        email.value = '';
        contactAboutBeta.value = false;
        comments.value = '';
        emailError.value = null;
        submitting.value = false;
        hasSubmitted.value = false;
        alreadyRegistered.value = false;
    };

    const submit = async () => {
        emailError.value = validateEmail(email.value);
        if (emailError.value) {
            return;
        }

        submitting.value = true;

        try {
            const normalizedComments = comments.value.trim() ? comments.value.trim() : null;
            const response = await waitlistEntryService.joinWaitlist(email.value.trim(), normalizedComments, contactAboutBeta.value);
            alreadyRegistered.value = response.already_registered;
            hasSubmitted.value = true;
            notifySuccess(alreadyRegistered.value ? "You're already on the waitlist." : "You're on the waitlist!");

            // Fire confetti only for new signups
            if (!alreadyRegistered.value) {
                setTimeout(() => fireConfetti(), 300);
            }

            queueMicrotask(() => {
                if (window.__LAGOMORPHA_FLAGS__?.environment !== 'local') {
                    try {
                        window.fbq?.('track', 'Lead');
                    } catch {}

                    try {
                        window.gtag?.('event', 'conversion', { send_to: 'AW-17897067454/hb2GCKKpnOsbEL6n_tVC' });
                    } catch {}
                }
            });
        } catch (err: any) {
            notifyError(err?.response?.data?.message ?? 'Unable to join the waitlist. Please try again.');
        } finally {
            submitting.value = false;
        }
    };

    watch(dialogValue, (open) => {
        if (open) {
            resetState();
            nextTick(() => {
                emailInput.value?.focus();
            });
        }
    });
</script>

<style lang="scss" scoped>
    @keyframes heartbeat {
        0% {
            transform: scale(1);
        }
        14% {
            transform: scale(1.3);
        }
        28% {
            transform: scale(1);
        }
        42% {
            transform: scale(1.3);
        }
        70% {
            transform: scale(1);
        }
    }

    .heartbeat {
        animation: heartbeat 1.5s ease-in-out infinite both;
    }
</style>
